import {
  Backdrop,
  Box,
  CircularProgress,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import ListTemplates from './components/listTemplates';
import useEmailTemplates from './hooks/email-templates-hooks';
import NotificationSelected from './components/notificationSelected';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import ModalCreateNotification from './components/modal-create-notification';
import ModalEditNotification from './components/modal-edit-notification';
import { APP_HEADER_HEIGHT_MD } from '../../layouts/authneticated-layout/constants';

// The route fills the rest of the viewport below the header spacer so the list/editor own
// their scroll instead of the document.
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      zIndex: theme.zIndex.drawer + 10000,
      color: theme.palette.common.white,
    },
    root: {
      '--email-templates-spacer': `${theme.spacing(7)}`,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      minHeight: 0,
      padding: theme.spacing(2),
      height: 'calc(100vh - var(--email-templates-spacer))',
      [theme.breakpoints.up('md')]: {
        '--email-templates-spacer': `${APP_HEADER_HEIGHT_MD}px`,
      },
    },
    '@supports (height: 100dvh)': {
      root: {
        height: 'calc(100dvh - var(--email-templates-spacer))',
      },
    },
    container: {
      display: 'flex',
      gap: theme.spacing(2),
      flex: 1,
      minHeight: 0,
      minWidth: 0,
    },
    listPane: {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 100%',
      minHeight: 0,
      minWidth: 0,
      [theme.breakpoints.up('sm')]: { flexBasis: '33.3333%' },
      [theme.breakpoints.up('md')]: { flexBasis: '25%' },
      [theme.breakpoints.up('lg')]: { flexBasis: '16.6667%' },
    },
    detailPane: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: 0,
      minWidth: 0,
    },
    detailBody: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      minWidth: 0,
    },
  })
);

const EmailTemplates = () => {
  const classes = useStyles();
  const theme = useTheme();
  // Phone = xs (0-599px): the list stays visible and the editor opens in a modal.
  // `noSsr` resolves the query on first render.
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const {
    notifications,
    isLoadingNotifications,
    isNotificationsError,
    refetchNotifications,
    isSubmitting,
    setSelectedNotification,
    selectedNotification,
    handleUpdateCreateNotification,
    confirmationModal,
    setConfirmationModal,
    handleDeleteNotification,
    t,
    setOpen,
    open,
    tagsField,
  } = useEmailTemplates();

  const { openConfirmation } = confirmationModal;

  return (
    <Box className={classes.root}>
      <div className={classes.container}>
        <div className={classes.listPane}>
          <ListTemplates
            notifications={notifications}
            setSelectedNotification={setSelectedNotification}
            selectedNotification={selectedNotification}
            setConfirmationModal={setConfirmationModal}
            setOpen={setOpen}
            isLoading={isLoadingNotifications}
            isError={isNotificationsError}
            onRetry={() => {
              refetchNotifications();
            }}
          />
        </div>
        {!isPhone && selectedNotification && (
          <div className={classes.detailPane}>
            <div className={classes.detailBody}>
              <NotificationSelected
                notification={selectedNotification}
                handleUpdateNotification={handleUpdateCreateNotification}
                tagsField={tagsField}
                t={t}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        title={t('emailTemplates:title')}
        confirmationMessage={t('emailTemplates:confirmationMessage')}
        isOpen={openConfirmation}
        onConfirm={handleDeleteNotification}
        onClose={() => setConfirmationModal({ openConfirmation: false, id: '' })}
        isRemoveLoading={false}
        buttonConfirmText={t('common:delete')}
      />

      <ModalCreateNotification
        open={open}
        handleClose={() => setOpen(false)}
        handleUpdateCreateNotification={handleUpdateCreateNotification}
        t={t}
        tagsField={tagsField}
        isSubmitting={isSubmitting}
      />

      <ModalEditNotification
        open={isPhone && !!selectedNotification}
        notification={selectedNotification}
        handleClose={() => setSelectedNotification(undefined)}
        handleUpdateNotification={handleUpdateCreateNotification}
        t={t}
        tagsField={tagsField}
        isSubmitting={isSubmitting}
      />

      {/* Interim double-submit shield for mutations only; query loading is inline in the list. */}
      <Backdrop className={classes.backdrop} open={isSubmitting}>
        <CircularProgress color="secondary" />
      </Backdrop>
    </Box>
  );
};

export default EmailTemplates;
